import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Styled from './ImpactDonationAmount.styled';
import { removeInvalidInputCharacters } from './utils/removeInvalidInputCharacters';
import { getNumberFromCurrencyString } from '../../utils/getNumberFromCurrencyString';
import { FREQUENCY } from '../../../../models/DonationFrequency';
import { getCurrencySymbol } from '../../../../features/passport/getCurrencySymbol';
import { convertToFormattedCurrencyString } from './utils/convertToFormattedCurrencyString';
import * as SkeletonLoader from '../SkeletonLoader';
import { useResizeFontToFit } from '../../../../features/passport/useResizeFontToFit';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The static display text or the editable other-amount input displaying the Donation Amount.
 *
 * When "Other" is selected in the DonationAmountRadioGroup, the input rendered by this component is automatically focused.
 */
export const ImpactDonationAmount = ({
  isEditable,
  value,
  locale,
  currencyCode,
  frequency,
  inputRef,
  dispatch,
  disabled,
  blockId,
  shouldForceFocusStyling,
  shouldShowErrorState,
  maxDonationAmount,
  loadingState
}) => {
  const {
    t
  } = useTranslation('blocks.impact');
  const [isInputFocused, setIsInputFocused] = React.useState(false);
  const containerRef = React.useRef(null);
  useResizeFontToFit({
    containerRef,
    elementClassName: 'impact-input-amount',
    // Keep in sync with Styled.OtherAmountInput
    defaultFontSize: 50,
    externalDependency: value,
    /**
     * The input has an explicit width so that the currency code immediately follows it.
     * Before resizing the text, set the width to 100%, otherwise the text may be measured
     * in a space that is smaller than the actual available width.
     */
    onBeforeResizeElementText: element => element.style.setProperty('width', '100%'),
    onAfterResizeElementText: (element, finalTextWidth) => element.style.setProperty('width', `min(${finalTextWidth}px, 100%)`)
  });
  useResizeFontToFit({
    containerRef,
    elementClassName: 'impact-static-amount',
    // Keep in sync with Styled.StaticAmount
    defaultFontSize: 50,
    externalDependency: value
  });
  const handleInputFocus = () => {
    setIsInputFocused(true);
  };
  const handleInputBlur = e => {
    setIsInputFocused(false);
    const numberString = convertToFormattedCurrencyString(e.target.value, currencyCode, locale);
    if (value !== numberString) {
      dispatch({
        type: 'SET_OTHER_AMOUNT_DISPLAY_VALUE',
        value: numberString
      });
    }
    dispatch({
      type: 'TOUCH_ERRORABLE_INPUT',
      input: 'otherAmountInput'
    });
  };
  const handleInputChange = e => {
    // This won't modify state if other amount is already selected
    dispatch({
      type: 'SELECT_OTHER_AMOUNT_BUTTON'
    });
    const numberString = removeInvalidInputCharacters(e.target.value);
    const doesNewNumberExceedMaxOtherAmount = getNumberFromCurrencyString(numberString, currencyCode) > maxDonationAmount;

    // Don't register any user inputs that cause the other amount to exceed max allowed amount
    if (doesNewNumberExceedMaxOtherAmount) return;
    dispatch({
      type: 'SET_OTHER_AMOUNT_DISPLAY_VALUE',
      value: numberString
    });
  };
  useEffect(() => {
    // if shouldShowErrorState === true then focus the cursor on the input field for "Other" amount.
    if (shouldShowErrorState) {
      inputRef.current?.focus();
    }
  }, [shouldShowErrorState, inputRef]);
  if (loadingState === 'loading') {
    return /*#__PURE__*/_jsx(SkeletonLoader.ImpactDonationAmountContainer, {});
  }
  return /*#__PURE__*/_jsxs(Styled.ImpactDonationAmountContainer, {
    ref: containerRef,
    shouldUseFocusStyling: isInputFocused || shouldForceFocusStyling,
    shouldShowErrorState: shouldShowErrorState,
    doFadeIn: loadingState === 'loaded',
    children: [/*#__PURE__*/_jsx(Styled.CurrencySymbolContainer, {
      shouldInheritColor: !isInputFocused && !shouldForceFocusStyling,
      children: getCurrencySymbol(currencyCode, locale)
    }), isEditable ? /*#__PURE__*/_jsx(Styled.OtherAmountInput, {
      ref: inputRef,
      className: "impact-input-amount",
      "data-id": `cp-impact-${frequency === FREQUENCY.ONE_TIME ? 'onetime' : 'recurring'}-custom-amount`,
      "aria-label": t('ImpactDonationAmount.amountInputAriaLabel', 'Amount input'),
      type: "text",
      value: value,
      disabled: disabled,
      onFocus: handleInputFocus,
      onBlur: handleInputBlur,
      onChange: handleInputChange,
      placeholder: t('ImpactDonationAmount.inputPlaceholder', '0'),
      shouldForceFocusStyling: shouldForceFocusStyling,
      shouldShowErrorState: shouldShowErrorState,
      inputMode: "decimal",
      "aria-invalid": shouldShowErrorState,
      "aria-errormessage": `${blockId}-error-message`
    }) : /*#__PURE__*/_jsx(Styled.StaticAmountContainer, {
      className: "impact-static-amount",
      children: /*#__PURE__*/_jsx(Styled.StaticAmount, {
        children: value
      })
    }), /*#__PURE__*/_jsx(Styled.CurrencyCodeContainer, {
      shouldInheritColor: !isInputFocused && !shouldForceFocusStyling,
      children: currencyCode
    })]
  });
};